import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Select, Button, Space, message, Tag, Spin,
    Modal, Descriptions, Typography, Form, Input, InputNumber, Alert
} from 'antd';
import {
    ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined,
    SyncOutlined, FileTextOutlined, CodeOutlined, CloudUploadOutlined
} from '@ant-design/icons';
import hostService from '../../../shared/services/hostService';

const { Option } = Select;
const { Text } = Typography;

// 应用类型配置
const APP_TYPES = [
    { key: 'php-fpm', name: 'PHP-FPM', icon: '🐘', description: 'PHP FastCGI Process Manager' },
    { key: 'nodejs', name: 'Node.js', icon: '💚', description: 'Node.js app service' },
    { key: 'python', name: 'Python', icon: '🐍', description: 'Python WSGI/ASGI app' },
    { key: 'golang', name: 'Golang', icon: '🔵', description: 'Go app service' },
];

const AppManager = ({ containerId: propContainerId, embedded = false }) => {
    const { t } = useTranslation();
    const [containers, setContainers] = useState([]);
    const [selectedContainer, setSelectedContainer] = useState(propContainerId || null);
    const [appStatuses, setAppStatuses] = useState({});
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState({});
    const [logModalVisible, setLogModalVisible] = useState(false);
    const [currentLogs, setCurrentLogs] = useState('');
    const [currentAppType, setCurrentAppType] = useState('');
    const [deployModalVisible, setDeployModalVisible] = useState(false);
    const [deployApp, setDeployApp] = useState(null);
    const [deploying, setDeploying] = useState(false);
    const [deployForm] = Form.useForm();

    // 各运行时的默认启动命令占位
    const DEPLOY_HINTS = {
        nodejs: { cmd: 'node app.js', hint: t('e.g. node app.js / npm start') },
        python: { cmd: 'python app.py', hint: t('e.g. python app.py / gunicorn app:app -b 0.0.0.0:$PORT') },
        golang: { cmd: './app', hint: t('e.g. ./app (upload/compile the binary in File Manager first)') },
    };

    const openDeploy = (appKey) => {
        setDeployApp(appKey);
        deployForm.setFieldsValue({
            command: DEPLOY_HINTS[appKey]?.cmd || '',
            working_dir: '/var/www/html',
            port: 3000,
        });
        setDeployModalVisible(true);
    };

    const handleDeploy = async () => {
        try {
            const v = await deployForm.validateFields();
            setDeploying(true);
            const res = await hostService.createApp({
                container_id: selectedContainer,
                type: deployApp,
                config: v.command,
                entry_point: v.command,
                working_dir: v.working_dir,
                port: v.port,
            });
            if (res.success) {
                message.success(t('App deployed and started'));
                setDeployModalVisible(false);
                loadAllAppStatuses();
            } else {
                message.error(res.error || t('Deployment failed'));
            }
        } catch (e) {
            if (e?.errorFields) return;
            message.error(e?.message || t('Deployment failed'));
        } finally {
            setDeploying(false);
        }
    };

    useEffect(() => {
        if (embedded && propContainerId) {
            setSelectedContainer(propContainerId);
            return;
        }
        loadContainers();
        const urlParams = new URLSearchParams(window.location.search);
        const containerIdParam = urlParams.get('container_id');
        if (containerIdParam) {
            setSelectedContainer(parseInt(containerIdParam));
        }
    }, [propContainerId, embedded]);

    useEffect(() => {
        if (selectedContainer) {
            loadAllAppStatuses();
        }
    }, [selectedContainer]);

    const loadContainers = async () => {
        try {
            const response = await hostService.getContainers();
            if (response.success) {
                setContainers(response.data || []);
            }
        } catch (error) {
            message.error(t('Failed to retrieve containers'));
        }
    };

    const loadAllAppStatuses = async () => {
        if (!selectedContainer) return;
        setLoading(true);
        const statuses = {};

        for (const app of APP_TYPES) {
            try {
                const response = await hostService.getAppStatus(selectedContainer, app.key);
                if (response.success) {
                    statuses[app.key] = response.data || { running: false };
                }
            } catch (error) {
                statuses[app.key] = { running: false, error: error.message };
            }
        }

        setAppStatuses(statuses);
        setLoading(false);
    };

    const handleStart = async (type) => {
        setActionLoading(prev => ({ ...prev, [type]: true }));
        try {
            const response = await hostService.startApp(selectedContainer, type);
            if (response.success) {
                message.success(t('App started'));
                loadAllAppStatuses();
            } else {
                message.error(response.error || t('Failed to start'));
            }
        } catch (error) {
            message.error(t('Failed to start'));
        } finally {
            setActionLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleStop = async (type) => {
        setActionLoading(prev => ({ ...prev, [type]: true }));
        try {
            const response = await hostService.stopApp(selectedContainer, type);
            if (response.success) {
                message.success(t('App stopped'));
                loadAllAppStatuses();
            } else {
                message.error(response.error || t('Failed to stop'));
            }
        } catch (error) {
            message.error(t('Failed to stop'));
        } finally {
            setActionLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleRestart = async (type) => {
        setActionLoading(prev => ({ ...prev, [type]: true }));
        try {
            const response = await hostService.restartApp(selectedContainer, type);
            if (response.success) {
                message.success(t('App restarted'));
                loadAllAppStatuses();
            } else {
                message.error(response.error || t('Restart failed'));
            }
        } catch (error) {
            message.error(t('Restart failed'));
        } finally {
            setActionLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleViewLogs = async (type) => {
        setCurrentAppType(type);
        setCurrentLogs(t('Loading...'));
        setLogModalVisible(true);

        try {
            const response = await hostService.getAppLogs(selectedContainer, type, 200);
            if (response.success) {
                setCurrentLogs(response.data?.logs || t('No logs yet'));
            } else {
                setCurrentLogs(t('Failed to retrieve logs: {{error}}', { error: response.error }));
            }
        } catch (error) {
            setCurrentLogs(t('Failed to retrieve logs: {{message}}', { message: error.message }));
        }
    };

    const getStatusTag = (status) => {
        if (status?.running) {
            return <Tag color="success">{t('Running')}</Tag>;
        }
        if (status?.error) {
            return <Tag color="error">{t('Error')}</Tag>;
        }
        return <Tag color="default">{t('Stopped')}</Tag>;
    };

    const AppCard = ({ app }) => {
        const status = appStatuses[app.key] || {};
        const isLoading = actionLoading[app.key];

        return (
            <Card
                title={
                    <Space>
                        <span style={{ fontSize: 20 }}>{app.icon}</span>
                        <span>{app.name}</span>
                    </Space>
                }
                extra={getStatusTag(status)}
                style={{ marginBottom: 16 }}
            >
                <Descriptions column={1} size="small">
                    <Descriptions.Item label={t('Description')}>
                        {t(app.description)}
                    </Descriptions.Item>
                    {status.version && (
                        <Descriptions.Item label={t('Version')}>
                            {status.version}
                        </Descriptions.Item>
                    )}
                    {status.pid && (
                        <Descriptions.Item label="PID">
                            {status.pid}
                        </Descriptions.Item>
                    )}
                    {status.uptime && (
                        <Descriptions.Item label={t('Uptime')}>
                            {status.uptime}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <div style={{ marginTop: 16 }}>
                    <Space>
                        {status.deployed === false ? (
                            // 运行时(Node/Python/Go)尚未部署应用:引导部署
                            <Button
                                type="primary"
                                icon={<CloudUploadOutlined />}
                                onClick={() => openDeploy(app.key)}
                            >
                                {t('Deploy app')}
                            </Button>
                        ) : !status.running ? (
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                loading={isLoading}
                                onClick={() => handleStart(app.key)}
                            >
                                {t('Start')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    icon={<PauseCircleOutlined />}
                                    loading={isLoading}
                                    onClick={() => handleStop(app.key)}
                                >
                                    {t('Stop')}
                                </Button>
                                <Button
                                    icon={<SyncOutlined />}
                                    loading={isLoading}
                                    onClick={() => handleRestart(app.key)}
                                >
                                    {t('Restart')}
                                </Button>
                            </>
                        )}
                        <Button
                            icon={<FileTextOutlined />}
                            onClick={() => handleViewLogs(app.key)}
                        >
                            {t('Logs')}
                        </Button>
                    </Space>
                </div>
            </Card>
        );
    };

    return (
        <div>
            {!embedded && <h2>{t('App management')}</h2>}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    {!embedded && (
                        <Col span={8}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder={t('Select a container')}
                                value={selectedContainer}
                                onChange={setSelectedContainer}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {containers.map(c => (
                                    <Option key={c.id} value={c.id}>
                                        {c.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                    <Col span={embedded ? 24 : 16}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadAllAppStatuses}
                            disabled={!selectedContainer}
                            loading={loading}
                        >
                            {t('Refresh status')}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {selectedContainer && (
                <Spin spinning={loading}>
                    <Row gutter={16}>
                        {APP_TYPES.map(app => (
                            <Col xs={24} sm={12} lg={6} key={app.key}>
                                <AppCard app={app} />
                            </Col>
                        ))}
                    </Row>
                </Spin>
            )}

            {/* 日志查看Modal */}
            <Modal
                title={t('{{currentAppType}} logs', { currentAppType })}
                open={logModalVisible}
                onCancel={() => setLogModalVisible(false)}
                footer={[
                    <Button key="refresh" onClick={() => handleViewLogs(currentAppType)}>
                        {t('Refresh')}
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setLogModalVisible(false)}>
                        {t('Close')}
                    </Button>
                ]}
                width={800}
            >
                <pre style={{
                    maxHeight: 500,
                    overflow: 'auto',
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    padding: 16,
                    borderRadius: 4,
                    fontSize: 12,
                    fontFamily: 'Monaco, Consolas, monospace'
                }}>
                    {currentLogs}
                </pre>
            </Modal>

            {/* 部署应用 Modal */}
            <Modal
                title={t('Deploy {{name}} app', { name: APP_TYPES.find(a => a.key === deployApp)?.name || '' })}
                open={deployModalVisible}
                onCancel={() => setDeployModalVisible(false)}
                onOk={handleDeploy}
                confirmLoading={deploying}
                okText={t('Deploy and start')}
                width={560}
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('Upload your code to the working directory in File Manager and enter the startup command. After deployment, the container\'s process supervisor starts the app automatically and keeps it running.')}
                />
                <Form form={deployForm} layout="vertical">
                    <Form.Item
                        name="command"
                        label={t('Start command')}
                        rules={[{ required: true, message: t('Please enter a startup command') }]}
                        extra={DEPLOY_HINTS[deployApp]?.hint}
                    >
                        <Input placeholder="node app.js" />
                    </Form.Item>
                    <Form.Item
                        name="working_dir"
                        label={t('Working directory')}
                        rules={[{ required: true, message: t('Please enter a working directory') }]}
                    >
                        <Input placeholder="/var/www/html" />
                    </Form.Item>
                    <Form.Item
                        name="port"
                        label={t('Listening port')}
                        extra={t('Port the app listens on (used to reverse proxy to your domain). Leave the default if there is no HTTP port.')}
                    >
                        <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AppManager;
